/**
 * Admin Dashboard
 *
 * Landing page for the admin section with links to sub-pages.
 */

import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Users,
  Package,
  Settings,
  BarChart3,
  Building2,
  Tags,
  Columns3,
  LayoutTemplate,
  Globe,
  Database,
  MousePointerClick,
  MapPinned,
  PanelTop,
  Languages,
  FileText,
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface NavItem {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
  disabled?: boolean;
  superOnly?: boolean;
  /** Renders a plain anchor (opens in a new tab) instead of a router Link. */
  external?: boolean;
}

const navItems: NavItem[] = [
  { path: '/admin',           label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/estimates', label: 'Estimates', icon: ClipboardList },
  { path: '/admin/users',     label: 'Users',     icon: Users,     disabled: true },
  { path: '/admin/retailers', label: 'Retailers', icon: Building2 },
  { path: '/admin/products',  label: 'Products',  icon: Package },
  { path: '/admin/product-fields', label: 'Product Fields', icon: Tags, superOnly: true },
  { path: '/admin/column-layout', label: 'Column Layout', icon: Columns3, superOnly: true },
  { path: '/admin/custom-tables', label: 'Custom Tables', icon: LayoutTemplate, superOnly: true },
  { path: '/admin/action-buttons', label: 'Action Buttons', icon: MousePointerClick, superOnly: true },
  { path: '/admin/maps', label: 'Location Maps', icon: MapPinned, superOnly: true },
  { path: '/admin/output-sections', label: 'Output Sections', icon: FileText, superOnly: true },
  { path: '/admin/site-content', label: 'Menu, Splash & Footer', icon: PanelTop, superOnly: true },
  { path: '/admin/locations', label: 'Locations', icon: Globe, superOnly: true },
  { path: '/admin/user-categories', label: 'User Categories', icon: Tags, superOnly: true },
  { path: '/admin/locales', label: 'Locales', icon: Globe, superOnly: true },
  { path: '/admin/translations', label: 'Translations', icon: Languages, superOnly: true },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/settings',  label: 'Settings',  icon: Settings, superOnly: true },
  // Runs migrate.php in a new tab — authorized by the super-admin Keycloak
  // session (no migration key needed when signed in as a super admin).
  { path: `${API_URL}/api/migrate.php`, label: 'Run Migrations', icon: Database, superOnly: true, external: true },
];

export const AdminDashboard: React.FC = () => {
  const location = useLocation();
  const { isSuperAdmin } = useAdminAuth();
  const isExactAdmin = location.pathname === '/admin';
  const visibleNav = navItems.filter(item => !item.superOnly || isSuperAdmin);

  return (
    <div className="container-fluid py-3">
      <div className="row g-3">
        {/* Sidebar */}
        <div className="col-12 col-md-3 col-lg-2">
          <div className="bg-white rounded-3 shadow-sm p-3">
            <h6 className="text-uppercase text-muted small fw-bold mb-3 d-flex align-items-center gap-2">
              <Settings size={14} /> Admin Panel
            </h6>
            <nav className="nav flex-column gap-1">
              {visibleNav.map(item => {
                const Icon = item.icon;
                const isActive = !item.external && (item.exact
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path));
                const className = `nav-link d-flex align-items-center gap-2 rounded px-2 py-2 small ${
                  isActive ? 'bg-tc-yellow text-dark fw-semibold' : 'text-secondary'
                } ${item.disabled ? 'opacity-50 pe-none' : ''}`;

                if (item.external) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                    >
                      <Icon size={16} />
                      {item.label}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.disabled ? '#' : item.path}
                    className={className}
                    onClick={item.disabled ? (e) => e.preventDefault() : undefined}
                  >
                    <Icon size={16} />
                    {item.label}
                    {item.disabled && (
                      <span className="badge bg-secondary ms-auto" style={{ fontSize: '0.6rem' }}>Soon</span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="col-12 col-md-9 col-lg-10">
          {isExactAdmin ? (
            <div className="bg-white rounded-3 shadow-sm p-4">
              <h2 className="fw-bold mb-1">Admin Dashboard</h2>
              <p className="text-muted mb-4">Manage your estimator application.</p>

              <div className="row g-3">
                <div className="col-12 col-sm-6 col-lg-4">
                  <Link to="/admin/estimates" className="text-decoration-none">
                    <div className="border rounded-3 p-4 text-center h-100 hover-shadow">
                      <ClipboardList size={32} className="text-primary mb-2" />
                      <h5 className="fw-semibold text-dark">Estimates</h5>
                      <p className="text-muted small mb-0">View and manage saved estimates</p>
                    </div>
                  </Link>
                </div>
                <div className="col-12 col-sm-6 col-lg-4">
                  <div className="border rounded-3 p-4 text-center h-100 opacity-50">
                    <Users size={32} className="text-secondary mb-2" />
                    <h5 className="fw-semibold text-dark">Users</h5>
                    <p className="text-muted small mb-0">Coming soon</p>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-lg-4">
                  <Link to="/admin/retailers" className="text-decoration-none">
                    <div className="border rounded-3 p-4 text-center h-100 hover-shadow">
                      <Building2 size={32} className="text-primary mb-2" />
                      <h5 className="fw-semibold text-dark">Retailers</h5>
                      <p className="text-muted small mb-0">Retailer directory (full edits coming)</p>
                    </div>
                  </Link>
                </div>
                <div className="col-12 col-sm-6 col-lg-4">
                  <Link to="/admin/products" className="text-decoration-none">
                    <div className="border rounded-3 p-4 text-center h-100 hover-shadow">
                      <Package size={32} className="text-primary mb-2" />
                      <h5 className="fw-semibold text-dark">Products</h5>
                      <p className="text-muted small mb-0">Master catalog and MSRP pricing</p>
                    </div>
                  </Link>
                </div>
                <div className="col-12 col-sm-6 col-lg-4">
                  <Link to="/admin/analytics" className="text-decoration-none">
                    <div className="border rounded-3 p-4 text-center h-100 hover-shadow">
                      <BarChart3 size={32} className="text-primary mb-2" />
                      <h5 className="fw-semibold text-dark">Analytics</h5>
                      <p className="text-muted small mb-0">See estimate trends and breakdowns</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
